import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import { Button } from '@asu/components-core/dist/libCore.es';
import Multiple from './RequirementMultiple';
import StaticData from './StaticData';
import Component from './Component';
import useServiceAuth from 'auth/useServiceAuth';
import useSafeMutation from 'auth/useSafeMutation';
import { isAuthError, cleanupSessionStorage, showRestorationAlert, parseSessionData, getCurrentUserFromToken } from 'auth/ServiceAuthUtils';
import {
  componentGet,
  componentGetAll,
  componentSave,
  componentUpdate,
} from 'apis/componentAPIs';
import {
  clearComponent,
  setAll,
  setSubsections,
} from 'state/slices/componentSlice';
import { setComponentList } from 'state/slices/dataListsSlice';
import {
  addTemplateComponent,
  editTemplateComponent,
  setAll as setAllTemplate,
} from 'state/slices/templateSlice';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const ComponentForm = ({
  id = null,
  handleClose = null,
  addToTemplate = false,
  deferSave = false,
}) => {
  const { getAccessToken } = useServiceAuth();
  // For new components, flag is already set manually to 'new' before save
  const { executeMutation: safeSaveComponent } = useSafeMutation(
    componentSave,
    'component-form-data'
  );
  // For existing components, use componentId from dataToSave as backup
  const { executeMutation: safeUpdateComponent } = useSafeMutation(
    componentUpdate,
    'component-form-data',
    { flagKey: 'component-modal-reopen', valueField: 'componentId' }
  );
  const [dataToModal, setDataToModal] = useState({});

  const year = useSelector((state) =>
    StaticData.yearList.find((yearObj) => yearObj.value === state.settings.year)
  );
  const componentName = useSelector((state) => state.component.componentName);
  const notes = useSelector((state) => state.component.notes);
  const componentSettings = useSelector(
    (state) => state.component.componentSettings
  );
  const subsections = useSelector((state) => state.component.subsections);
  const folder = useSelector((state) => state.component.folder);

  // Also access parent TemplateForm state to preserve it during auth redirect
  const templateState = useSelector((state) => ({
    templateId: state.template.templateId,
    templateYear: state.template.year,
    templateName: state.template.templateName,
    templateProgramType: state.template.templateProgramType,
    templateComponentIds: state.template.templateComponentIds || [],
    templateComponentObjects: state.template.templateComponentObjects || {},
    templateSettings: state.template.templateSettings,
    templateValidationList: state.template.templateValidationList || {},
    templateTextOptions: state.template.templateTextOptions || {},
  }));
  const catalogYear = useSelector((state) => state.settings.year);
  const [showAsComponent, setShowAsComponent] = useState(true);
  const [requirementType, setRequirementType] = useState(null);
  const [error, setError] = useState('');
  const [activeName, setActiveName] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const [acknowledgeChange, setAcknowledgeChange] = useState(false);
  const [dataWasRestored, setDataWasRestored] = useState(false);

  const dispatch = useDispatch();

  // Helper: Check if ComponentForm is being used in a template context (new or existing template)
  const hasTemplateContext = useMemo(() => {
    return templateState.templateId ||
      templateState.templateName !== 'Untitled template' ||
      (templateState.templateComponentIds && templateState.templateComponentIds.length > 0);
  }, [templateState.templateId, templateState.templateName, templateState.templateComponentIds]);

  // Helper: Save parent template data before mutations (if in template context)
  const saveTemplateDataIfNeeded = (location = 'mutation') => {
    if (!hasTemplateContext) {
      console.log(`[ComponentForm ${location}] No template context - skipping template data save`);
      return false;
    }

    const templateDataToSave = {
      templateName: templateState.templateName,
      programType: templateState.templateProgramType,
      year: catalogYear,
      templateId: templateState.templateId,
      componentIds: templateState.templateComponentIds,
      componentObjects: templateState.templateComponentObjects,
      settings: templateState.templateSettings,
      validationRules: templateState.templateValidationList,
      textOptions: templateState.templateTextOptions,
      _savedByUser: getCurrentUserFromToken(),
      _savedAt: new Date().toISOString()
    };

    sessionStorage.setItem('template-form-data', JSON.stringify(templateDataToSave));
    sessionStorage.setItem('template-form-should-restore', 'true');

    console.log(`[ComponentForm ${location}] Saved template data:`, {
      templateId: templateState.templateId,
      templateName: templateState.templateName,
      componentCount: templateState.templateComponentIds?.length || 0,
    });

    return true;
  };

  // Restore saved component data on mount (from useSafeMutation)
  useEffect(() => {
    const savedData = sessionStorage.getItem('component-form-data');
    const savedTemplateData = sessionStorage.getItem('template-form-data');
    const shouldRestore = sessionStorage.getItem(
      'template-form-should-restore'
    );
    const componentModalReopen = sessionStorage.getItem(
      'component-modal-reopen'
    );

    console.log(
      '[ComponentForm] Restoration check:',
      '\n  id prop:',
      id,
      '\n  component-form-data exists:',
      !!savedData,
      '\n  template-form-data exists:',
      !!savedTemplateData,
      '\n  template-form-should-restore:',
      shouldRestore,
      '\n  component-modal-reopen:',
      componentModalReopen,
      '\n  templateState.templateId:',
      templateState.templateId
    );

    if (savedTemplateData) {
      console.log(
        '[ComponentForm] Template data found:',
        JSON.parse(savedTemplateData)
      );
    } else {
      console.log('[ComponentForm] No template data in sessionStorage');
    }
    if (savedData) {
      try {
        const parsedData = parseSessionData('component-form-data');
        const savedId =
          parsedData?.id || parsedData?.jsonData?.id || parsedData?.componentId;
        console.log(
          'Parsed saved component data. Saved id:',
          savedId,
          'Current id prop:',
          id
        );

        // Only restore if the saved id matches the current id prop
        // This ensures we're restoring the right component's data
        if (!savedId || savedId === id) {
          console.log('IDs match, restoring component data');

          // Restore component through Redux setAll action
          dispatch(
            setAll({
              componentName: parsedData.componentName || '',
              notes: parsedData.notes || '',
              componentSettings: parsedData.settings || {},
              subsections: parsedData.subsections || {},
              folder: parsedData.folder || '',
            })
          );

          // If template data was also saved, restore it too
          if (savedTemplateData) {
            const parsedTemplateData = parseSessionData('template-form-data');
            console.log('Restoring template data:', parsedTemplateData);
            dispatch(
              setAllTemplate({
                templateName: parsedTemplateData.templateName || '',
                templateProgramType: parsedTemplateData.programType || '',
                year: parsedTemplateData.year || catalogYear,
                templateId: parsedTemplateData.templateId || '',
                templateComponentIds: parsedTemplateData.componentIds || [],
                templateComponentObjects:
                  parsedTemplateData.componentObjects || {},
                templateSettings: parsedTemplateData.settings || {},
                templateValidationList:
                  parsedTemplateData.validationRules || {},
                templateTextOptions: parsedTemplateData.textOptions || {},
              })
            );
          }

          // Set flag to prevent fetch from overwriting restored data
          setDataWasRestored(true);

          // Show restoration alert
          showRestorationAlert({
            formType: 'component',
            includeTemplate: !!savedTemplateData
          });

          // Clean up all restoration flags and data
          cleanupSessionStorage([
            'component-form-data',
            'template-form-data',
            'template-form-should-restore',
            'component-modal-reopen'
          ], 'ComponentForm restoration');
        } else {
          console.log(
            'ID mismatch, not restoring. Saved:',
            parsedData.id,
            'Current:',
            id
          );
        }
      } catch (error) {
        console.error('Error restoring saved component data:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, catalogYear]); // templateState.templateId intentionally omitted - only read once at mount

  /********** APIs **********/

  // Get all components after saving and dispatch component list to update component library
  const { mutate: getAllComponents } = useMutation({
    mutationFn: componentGetAll,
    onSuccess: async (data) => {
      const next = [];

      data.forEach((component) => {
        if (component['details']) {
          const jsonObject = JSON.parse(component['details'].S);
          const obj = {
            id: component['sk'].S,
            details: jsonObject,
            componentObj: component,
          };
          next.push(obj);
        }
      });

      dispatch(setComponentList(next));
    },
  });

  // Get new component after saving and add to template
  const { mutate: getSavedComponent } = useMutation({
    mutationFn: componentGet,
    onSuccess: async (data) => {
      if (addToTemplate)
        dispatch(addTemplateComponent({ id: data['sk'], object: data }));
    },
  });

  // Save new component to database
  // saveComponent will be called directly using safeSaveComponent
  // No need for separate useMutation wrapper

  // Get existing component after updating and update template
  const { mutate: getUpdatedComponent } = useMutation({
    mutationFn: componentGet,
    onSuccess: async (data) => {
      dispatch(editTemplateComponent({ id: data['sk'], object: data }));
    },
  });

  // updateComponent will be called directly using safeUpdateComponent
  // No need for separate useMutation wrapper

  // Get component to be shown to user
  const {
    data: componentData,
    error: getComponentError,
    isError: getComponentIsError,
    isSuccess: getComponentIsSuccess,
    isPending,
    fetchStatus,
    refetch: refetchComponent,
  } = useQuery({
    queryKey: ['component', id],
    queryFn: async () => {
      const token = await getAccessToken();
      return componentGet({
        id,
        token,
      });
    },
    enabled: !!id,
  });

  const modalSetAsComponent = () => {
    setRequirementType(null);
    setShowAsComponent(true);
    if (deferSave && id) {
      setPendingDeferSave(true);
    }
  };

  // When deferSave is true, auto-save to Redux and close modal after returning from requirement edit
  const [pendingDeferSave, setPendingDeferSave] = useState(false);

  useEffect(() => {
    if (pendingDeferSave && showAsComponent && deferSave && id) {
      const jsonData = {
        year: year?.value,
        componentName: componentName,
        notes: notes,
        settings: componentSettings,
        subsections: subsections,
        folder: folder,
        id: id,
      };
      const updatedObject = {
        ...templateState.templateComponentObjects?.[id],
        details: JSON.stringify(jsonData),
      };
      dispatch(editTemplateComponent({ id, object: updatedObject }));
      setPendingDeferSave(false);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeferSave, showAsComponent, subsections]);

  const modalSetAsRequirement = (type) => {
    setShowAsComponent(false);
    setRequirementType(type);
  };

  // const modalSetAsValidation = () => {
  //   setShowAsComponent(false);
  //   setShowAsValidation(true);
  // };

  // const modalSetAsSettings = () => {
  //   setShowAsComponent(false);
  //   setShowAsSettings(true);
  // };

  // const modalSetAsNotes = () => {
  //   setShowAsComponent(false);
  //   setShowAsNotes(true);
  // };

  const handleSaveComponent = async () => {
    if (componentName) {
      const jsonData = {
        year: year.value,
        componentName: componentName,
        notes: notes,
        settings: componentSettings,
        subsections: subsections,
        folder: folder,
      };

      console.log('JSON: ', jsonData);

      try {
        // Save parent TemplateForm state if in template context
        saveTemplateDataIfNeeded('handleSaveComponent');

        // Set flag to reopen modal after redirect (before potential auth failure)
        sessionStorage.setItem('component-modal-reopen', 'new');

        // Prepare data for restoration
        const dataToSave = {
          componentName,
          notes,
          settings: componentSettings,
          subsections,
          folder,
          year: year.value,
          jsonData,
        };

        const result = await safeSaveComponent({ jsonData }, dataToSave);

        if (result) {
          const sectionId = result.split(' ')[2].trim();
          console.log('SECTION ID: ', sectionId);
          const newId = sectionId.replace('COMPONENT-', '');
          console.log('ID: ', newId);

          // Update subsection ids
          updateSubsectionIdToSectionId(newId);

          const token = await getAccessToken();
          getAllComponents({ token });
          getSavedComponent({ id: newId, token });

          // Clear the reopen flag on successful save
          sessionStorage.removeItem('component-modal-reopen');

          handleClose();
          dispatch(clearComponent());
        }
      } catch (error) {
        console.error('Error saving component:', error);

        // Check if this is an auth/session error
        if (error.message && !isAuthError(error)) {
          setError(error.message);
        }
      }
    } else {
      alert('A section name is required');
    }
  };

  const handleUpdateComponent = async () => {
    if (componentName) {
      const jsonData = {
        year: year.value,
        componentName: componentName,
        notes: notes,
        settings: componentSettings,
        subsections: subsections,
        folder: folder,
        id: id,
      };

      console.log('JSON: ', jsonData);

      // If deferSave is true (template context), save to Redux only
      if (deferSave) {
        const updatedObject = {
          ...templateState.templateComponentObjects?.[id],
          details: JSON.stringify(jsonData),
        };
        dispatch(editTemplateComponent({ id, object: updatedObject }));
        handleClose();
        return;
      }

      try {
        // Save parent TemplateForm state if in template context
        saveTemplateDataIfNeeded('handleUpdateComponent');

        // Set flag to reopen modal after redirect with component ID
        sessionStorage.setItem('component-modal-reopen', id);

        // Prepare data for restoration
        const dataToSave = {
          componentId: id,
          componentName,
          notes,
          settings: componentSettings,
          subsections,
          folder,
          year: year.value,
          jsonData,
        };

        const result = await safeUpdateComponent({ id, jsonData }, dataToSave);

        if (result) {
          const token = await getAccessToken();
          getUpdatedComponent({ id, token });

          // Clear the reopen flag on successful save
          sessionStorage.removeItem('component-modal-reopen');

          handleClose();
          dispatch(clearComponent());
        }
      } catch (error) {
        console.error('Error updating component:', error);

        // Check if this is an auth/session error
        if (error.message && !isAuthError(error)) {
          setError(error.message);
        }
      }
    } else {
      alert('A section name is required');
    }
  };

  const handleCloseComponentForm = () => {
    handleClose();
    dispatch(clearComponent());
  };

  // click Edit button to open modal and edit the data
  const openEditModal = (index, type, details, id, subsectionId) => {
    const modalData = {
      index: index,
      type: type,
      details: details,
      id: id,
      subsectionId: subsectionId,
    };

    console.log('dataToModal: ', modalData);
    setDataToModal(modalData);
    modalSetAsRequirement(type);
  };

  //add index and req type to data. We need to know the index when data sent back from child component
  //and what type of the requirment it is.
  const createWrappedData = (
    index,
    type,
    details,
    componentId,
    subsectionId,
    oldIndex,
    oldSubsectionId
  ) => {
    const wrappedData = {
      index: index,
      type: type,
      details: details,
      id: componentId,
      subsectionId: subsectionId,
      oldIndex: oldIndex,
      oldSubsectionId: oldSubsectionId,
    };
    console.log('wrappedData: ', wrappedData);
    return wrappedData;
  };

  // Callback function to receive data from the modal
  const receiveDataFromModal = (data) => {
    console.log('Received data from modal:' + JSON.stringify(data));
    if (data?.subsectionId) console.log('subsectionId: ', data.subsectionId);

    let subsectionId = '0';

    if (data?.subsectionId && data.subsectionId !== '999') {
      // update existing subsections
      subsectionId = data.subsectionId;
    } else if (
      // create new subsections
      data?.subsectionId === '999' &&
      data?.details?.subsectionName !== '' &&
      data?.details?.subsectionName !== undefined
    ) {
      subsectionId = uuidv4();
    } else {
      // update main section
      subsectionId = '0'; // when a new section is created, the subsection id is 0 for requirements in main section, it is a temporary id. It will be updated to the section id after the section is created.
    }

    console.log('new subsectionId: ', subsectionId);

    if (data) {
      updateData(data.index, data.type, data.details, id, subsectionId);
    }
  };

  // When a new section is created, update the subsection id to be the section id if the temporary subsection id is 0 (main section)
  const updateSubsectionIdToSectionId = async (sectionId) => {
    console.log(
      'update subsectionid to be sectionid: ' + JSON.stringify(subsections)
    );

    const newSubsections = { ...(subsections || {}) };

    // Get the main subsection with temporary id 0
    const mainSubsection = newSubsections['0'];
    if (mainSubsection) {
      // Update the subsection id to be the section id
      newSubsections[sectionId] = mainSubsection;
      // Delete the main subsection with temporary id 0
      delete newSubsections['0'];
      dispatch(setSubsections(newSubsections));

      const jsonData = {
        year: year.value,
        componentName: componentName,
        notes: notes,
        settings: componentSettings,
        subsections: newSubsections,
        folder: folder,
      };

      console.log('JSON: ', jsonData);

      try {
        // Save parent TemplateForm state if in template context
        saveTemplateDataIfNeeded('subsection update');

        const dataToSave = {
          componentId: id,
          componentName,
          notes,
          settings: componentSettings,
          subsections: newSubsections,
          folder,
          year: year.value,
          jsonData,
        };

        await safeUpdateComponent({ id, jsonData }, dataToSave);
      } catch (error) {
        console.error('Error updating component subsection:', error);

        // Check if this is an auth/session error
        if (error.message && !isAuthError(error)) {
          setError(error.message);
        }
      }
    } else {
      console.log('No subsection found with empty string key.');
    }
  };

  // this function is important !!! it update the template data with the new data from the modal
  // update subsections data for section in template
  const updateData = (index, type, newData, id, subsectionId) => {
    console.log('Updating data...');
    console.log(
      'Subsection ID: ' +
        subsectionId +
        ', ' +
        'Index: ' +
        index +
        ', Type: ' +
        type +
        ', New Data: ' +
        JSON.stringify(newData)
    );

    if (index === 9999 && subsections && subsections[subsectionId]) {
      console.log('THis is where it is messing up');
      index = subsections[subsectionId].requirements?.length || 0;
      console.log('index:', index);
    } else if (index === 9999) {
      index = 0;
    }

    const newSubsections = { ...(subsections || {}) };

    console.log('subsection: ', subsections[subsectionId]);

    const newDataList = subsections[subsectionId]?.requirements
      ? [...subsections[subsectionId].requirements]
      : [];

    if (!newData || !isEqual(newDataList[index], newData)) {
      if (!newData) {
        // if newData is empty (delete data)
        newDataList.splice(index, 1);
      } else {
        // if new data is not empty, then update data
        newDataList[index] = newData;
      }
    }

    const newObject = {
      ...subsections[subsectionId],
      requirements: newDataList,
      hours: subsections[subsectionId]?.hours || 0,
      upperDivisionHours: subsections[subsectionId]?.upperDivisionHours || 0,
    };

    newSubsections[subsectionId] = newObject;

    console.log('new subsection data: ', newSubsections);

    dispatch(setSubsections(newSubsections));
  };

  // this function is to update the subsection name and hours in template
  const updateSubsection = (data, subsectionId, id, action) => {
    console.log('Updating subsectionname...');
    console.log('subsectionSettingData: ', data);

    const newSubsections = { ...(subsections || {}) };
    const subId = subsectionId !== '999' ? subsectionId : uuidv4();

    console.log('subsectionId: ', subId);

    const newDataList = subsections[subId]?.requirements
      ? [...subsections[subId].requirements]
      : [];

    const newObject = {
      requirements: newDataList,
      subsectionName:
        action === 'subsectionName'
          ? data
          : subsections[subId]?.subsectionName || '',
      hours: action === 'hours' ? data : subsections[subId]?.hours || 0,

      upperDivisionHours:
        action === 'upperDivisionHours'
          ? data
          : subsections[subId]?.upperDivisionHours || 0,
    };

    newSubsections[subId] = newObject;

    console.log('new subsection data: ', newSubsections);

    dispatch(setSubsections(newSubsections));
  };

  const componentState = useSelector((state) => state.component);

  const details = useMemo(
    () => ({
      year: componentState.year,
      componentName: componentState.componentName,
      notes: componentState.notes,
      settings: componentState.componentSettings,
      subsections: componentState.subsections,
      folder: componentState.folder,
    }),
    [componentState]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getRequirementText = (req) => {
    let division = '';
    let checkCourses = '';

    if (req.division?.trim()) {
      division =
        req.division === 'Upper' || req.division === 'Lower'
          ? req.division + ' Division '
          : req.division + '-Level ';
    }

    if (
      req.checkType === 'gpa' &&
      req.checkSubtype === 'courses' &&
      !!req.checkCourses?.length
    ) {
      req.checkCourses.forEach((checkCourse, index, array) => {
        if (index !== 0 && index === array.length - 1) checkCourses += 'and ';

        checkCourses += checkCourse.subject + ' ' + checkCourse.number;

        if (array.length > 2 && index < array.length - 1) checkCourses += ', ';
        else checkCourses += ' ';
      });
    }

    if (req.itemType === 'req_course')
      return `${req.subject} ${req.number}: ${req.topic?.description ?? !!req.proposedTitle ? req.proposedTitle : req.description}`;
    if (req.itemType === 'req_gs') return `${division}${req.gs}`;
    if (req.itemType === 'req_elective')
      return `${req.subject ? req.subject + ' ' : ''}${division}Elective`;
    if (req.itemType === 'req_text')
      return `${division}${req.group ? req.group + ': ' : ''}${req.groupIndex === 'custom_text' ? req.customText : req.text}`;
    if (req.itemType === 'req_check') {
      const textDisplay = `${req.checkType === 'gpa' ? 'Check: ' : req.checkType === 'milestone' ? 'Milestone: ' : ''}${
        req.gpa ? `Minimum ${req.gpa} ` : ''
      }${req.checkSubtype === 'asu_cum_gpa' ? 'ASU Cumulative GPA' : ''}${
        req.checkSubtype === 'major_gpa' ? 'Major GPA' : ''
      }${req.checkSubtype === 'subject' ? 'GPA in all ' + req.subject + ' ' + division + 'courses' : ''}${
        req.checkSubtype === 'courses' ? `GPA in ${checkCourses}` : ''
      }${req.milestone ? `${Object.values(req.milestone)[0]}` : ''}`;
      console.log('Text displayed: ', textDisplay);
      return textDisplay;
    }

    return null;
  };

  function handleDragStart(event) {
    let activeIds = event.active.id.split('@');
    let activeReqId = activeIds[0];
    let activeSubsectionId = activeIds[1];

    const activeReq = details.subsections[activeSubsectionId].requirements.find(
      (req) => req.requirementId === activeReqId
    );
    let activeReqName = getRequirementText(activeReq.courses[0]);

    if (activeReq.courses.length > 1) activeReqName += '...';

    setActiveName(activeReqName);
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      let activeIds = active.id.split('@');
      let activeReqId = activeIds[0];
      let activeSubsectionId = activeIds[1];
      let overIds = over.id.split('@');
      let overReqId = overIds[0];
      let overSubsectionId = overIds[1];
      let activeReqIndex, overReqIndex;
      console.log('active: ', active);
      console.log('over: ', over);

      if (details?.subsections?.[activeSubsectionId]?.requirements)
        details.subsections[activeSubsectionId].requirements.forEach(
          (req, index) => {
            if (req.requirementId === activeReqId) {
              activeReqIndex = index;
            }
          }
        );

      if (details?.subsections?.[overSubsectionId]?.requirements)
        details.subsections[overSubsectionId].requirements.forEach(
          (req, index) => {
            if (req.requirementId === overReqId) {
              overReqIndex = index;
            }
          }
        );

      const updatedSubsections = { ...details.subsections };
      console.log(updatedSubsections);

      if (activeSubsectionId === overSubsectionId) {
        const activeSubsection = {
          ...updatedSubsections[activeSubsectionId],
        };

        activeSubsection.requirements = arrayMove(
          activeSubsection.requirements,
          activeReqIndex,
          overReqIndex
        );

        updatedSubsections[activeSubsectionId] = activeSubsection;
      } else {
        // Different subsections - move requirement between them
        const sourceSubsection = {
          ...updatedSubsections[activeSubsectionId],
        };
        console.log(sourceSubsection);
        const sourceRequirements = [...sourceSubsection.requirements];
        const targetSubsection = {
          ...updatedSubsections[overSubsectionId],
        };
        console.log(targetSubsection);
        const targetRequirements = [...targetSubsection.requirements];

        // Get the requirement to move
        console.log(sourceRequirements);
        console.log(activeReqIndex);
        const [movedRequirement] = sourceRequirements.splice(activeReqIndex, 1);

        // If overReqIndex is undefined, append to the end
        if (typeof overReqIndex === 'undefined') {
          targetRequirements.push(movedRequirement);
        } else {
          targetRequirements.splice(overReqIndex, 0, movedRequirement);
        }

        sourceSubsection.requirements = sourceRequirements;
        targetSubsection.requirements = targetRequirements;
        updatedSubsections[activeSubsectionId] = sourceSubsection;
        updatedSubsections[overSubsectionId] = targetSubsection;
      }

      dispatch(setSubsections(updatedSubsections));
    }
    setShowOverlay(false);
    setActiveName('');
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!active || !over) {
      setShowOverlay(true);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Split IDs to get subsection and requirement IDs
    let activeIds = activeId.split('@');
    let activeSubsectionId = activeIds[1];
    let overIds = overId.split('@');
    let overReqId = overIds[0];
    let overSubsectionId = overIds[1];

    if (activeSubsectionId !== overSubsectionId || !overReqId)
      setShowOverlay(true);
    else setShowOverlay(false);

    // // If dragging over a different subsection
    // if (activeSubsectionId !== overSubsectionId) {
    //   // Create a deep copy of the current subsections
    //   const updatedSubsections = { ...details.subsections };

    //   // Get source and target subsections
    //   const sourceSubsection = {
    //     ...updatedSubsections[activeSubsectionId],
    //   };
    //   const sourceRequirements = [...sourceSubsection.requirements];
    //   const targetSubsection = {
    //     ...updatedSubsections[overSubsectionId],
    //   };
    //   const targetRequirements = [...targetSubsection.requirements];

    //   // Find the requirement to move
    //   const draggedReqIndex = sourceRequirements.findIndex(
    //     (req) => req.requirementId === activeReqId
    //   );

    //   if (draggedReqIndex === -1) return;

    //   // Get the requirement and remove it from source
    //   const [draggedReq] = sourceRequirements.splice(draggedReqIndex, 1);

    //   // Find where to insert in target subsection
    //   const overReqIndex = targetRequirements.findIndex(
    //     (req) => req.requirementId === overReqId
    //   );

    //   // Insert the requirement at the correct position
    //   if (overReqIndex === -1) {
    //     targetRequirements.push(draggedReq);
    //   } else {
    //     targetRequirements.splice(overReqIndex, 0, draggedReq);
    //   }

    //   // Update both subsections in the state
    //   sourceSubsection.requirements = sourceRequirements;
    //   targetSubsection.requirements = targetRequirements;
    //   updatedSubsections[activeSubsectionId] = sourceSubsection;
    //   updatedSubsections[overSubsectionId] = targetSubsection;

    //   // Dispatch the update to Redux
    //   dispatch(setSubsections(updatedSubsections));
    // }
  };

  useEffect(() => {
    // Skip fetch if we just restored data from sessionStorage
    if (dataWasRestored) {
      console.log(
        'Skipping component fetch - data was restored from sessionStorage'
      );
      return;
    }

    if (!!id) {
      if (getComponentIsError) setError(getComponentError.message);

      if (getComponentIsSuccess) {
        console.log('componentData: ', componentData);

        const jsonDetails = JSON.parse(componentData['details']);
        const yearValue = StaticData.yearList.find(
          (obj) => obj.value === jsonDetails.year
        );

        dispatch(
          setAll({
            componentId: componentData['sk'],
            year: yearValue,
            componentName: jsonDetails.componentName,
            notes: jsonDetails.notes,
            componentSettings: jsonDetails.settings,
            // componentRequirementList: jsonDetails.requirements,
            subsections: jsonDetails.subsections,

            folder: jsonDetails.folder,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentData, getComponentIsSuccess, refetchComponent]);

  useEffect(() => {
    console.log(subsections);
  }, [subsections]);

  if (isPending && fetchStatus === 'fetching')
    return <div className="container">Loading...</div>;

  if (getComponentIsError)
    return (
      <div>
        <div>An error has occurred: {error}</div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <div style={{ width: '1224px' }}>
      {showAsComponent ? (
        <>
          <div className="m-6">
            <DndContext
              sensors={sensors}
              collisionDetection={pointerWithin}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              modifiers={[restrictToVerticalAxis]}
            >
              <DragOverlay
                style={{
                  width: '50%',
                }}
                dropAnimation={null}
              >
                {showOverlay ? (
                  <div className="px-4 py-2 fw-bold me-4 d-flex bg-white border border-gray1">
                    {activeName}
                  </div>
                ) : null}
              </DragOverlay>
              <Component
                id={id}
                year={year?.value}
                edit={true}
                openEditModal={openEditModal}
                // modalSetAsRequirement={modalSetAsRequirement}
                updateData={updateData}
                updateSubsection={updateSubsection}
              />
            </DndContext>
          </div>
          <div className="bg-gray-1 px-6 pt-3 pb-6">
            {/* TODO: */}
            {!deferSave && (
            <form className="uds-form">
              <div className="form-check ps-0 mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="acknowledgeChangeCheck"
                  checked={acknowledgeChange}
                  onChange={(e) => setAcknowledgeChange(e.target.checked)}
                />
                <label
                  className="form-check-label fw-normal"
                  style={{ fontSize: '14px', paddingTop: '4px' }}
                  htmlFor="acknowledgeChangeCheck"
                >
                  I acknowledge that changes to this section will apply to all
                  templates and checksheets using this section.
                </label>
              </div>
            </form>
            )}
            {/* {id && (
              <div className="d-flex gap-1 align-items-center mb-3">
                <FontAwesomeIcon icon={faInfoCircle} />
                <div>
                  Changes to this section will apply to all templates with
                  Catalog Year {year.label}
                </div>
              </div>
            )} */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-1 align-items-center">
                {!id ? (
                  <Button
                    onClick={handleSaveComponent}
                    label="Create new section"
                    color="maroon"
                  />
                ) : (
                  <Button
                    onClick={handleUpdateComponent}
                    label={deferSave ? "Save" : "Update in library"}
                    color="maroon"
                    disabled={!deferSave && !acknowledgeChange}
                  />
                )}
                <Button
                  onClick={handleCloseComponentForm}
                  label="Cancel"
                  color="gray"
                />
              </div>
              {/* TODO: */}
              {/* <div className="d-flex gap-1 align-items-center opacity-50">
                <FontAwesomeIcon icon={faTrash} className="text-gray-6" />
                <div className="button-link">Delete section from library</div>
              </div> */}
            </div>
          </div>
        </>
      ) : requirementType === 'req_multiple' ? (
        <Multiple
          componentId={id}
          year={year.value}
          componentName={componentName}
          dataFromParent={dataToModal}
          dataToParent={(newData) => receiveDataFromModal(newData)}
          createWrappedData={createWrappedData}
          exitRequirement={modalSetAsComponent}
        />
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default ComponentForm;
